import { useEffect, useRef, useState, useMemo, memo } from 'react';
import { User, Euro, TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const normalizeBalance = (value) => (Math.abs(value) < 0.01 ? 0 : value);

const getBalanceColor = (balance) => {
  const safeBalance = normalizeBalance(balance);
  if (safeBalance > 0) {
    return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40';
  } else if (safeBalance < 0) {
    return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40';
  }
  return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/40';
};

const getBalanceIcon = (balance) => {
  const safeBalance = normalizeBalance(balance);
  if (safeBalance > 0) {
    return <TrendingUp className="w-4 h-4" />;
  } else if (safeBalance < 0) {
    return <TrendingDown className="w-4 h-4" />;
  }
  return <Euro className="w-4 h-4" />;
};

const getBalanceText = (balance) => {
  const safeBalance = normalizeBalance(balance);
  if (safeBalance > 0) {
    return formatCurrency(safeBalance);
  } else if (safeBalance < 0) {
    return formatCurrency(Math.abs(safeBalance));
  }
  return 'In pari';
};

const PersonTile = memo(({ person, onPersonClick, onAddCredit, onAddDebt }) => {
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startXRef = useRef(null);
  const swipeXRef = useRef(0);
  const actionTriggeredRef = useRef(false);
  const moveListenerRef = useRef(null);
  const upListenerRef = useRef(null);
  const threshold = 60;

  const resetSwipe = () => {
    setSwiping(false);
    setSwipeX(0);
    swipeXRef.current = 0;
    startXRef.current = null;
  };

  const triggerActionIfNeeded = () => {
    const deltaX = swipeXRef.current;
    if (deltaX > threshold) {
      actionTriggeredRef.current = true;
      onAddCredit?.(person.name);
    } else if (deltaX < -threshold) {
      actionTriggeredRef.current = true;
      onAddDebt?.(person.name);
    }
  };

  const handleTouchStart = (e) => {
    setSwiping(true);
    startXRef.current = e.touches[0].clientX;
    actionTriggeredRef.current = false;
  };

  const handleTouchMove = (e) => {
    if (startXRef.current === null) return;
    const deltaX = e.touches[0].clientX - startXRef.current;
    swipeXRef.current = deltaX;
    setSwipeX(deltaX);
  };

  const handleTouchEnd = () => {
    if (startXRef.current === null) {
      resetSwipe();
      return;
    }
    triggerActionIfNeeded();
    resetSwipe();
  };

  const handleMouseDown = (e) => {
    setSwiping(true);
    startXRef.current = e.clientX;
    actionTriggeredRef.current = false;
    moveListenerRef.current = handleMouseMove;
    upListenerRef.current = handleMouseUp;
    window.addEventListener('mousemove', moveListenerRef.current);
    window.addEventListener('mouseup', upListenerRef.current);
  };

  const handleMouseMove = (e) => {
    if (startXRef.current === null) return;
    const deltaX = e.clientX - startXRef.current;
    swipeXRef.current = deltaX;
    setSwipeX(deltaX);
  };

  const handleMouseUp = () => {
    triggerActionIfNeeded();
    resetSwipe();
    window.removeEventListener('mousemove', moveListenerRef.current || handleMouseMove);
    window.removeEventListener('mouseup', upListenerRef.current || handleMouseUp);
    moveListenerRef.current = null;
    upListenerRef.current = null;
  };

  const handleClick = () => {
    if (actionTriggeredRef.current) {
      actionTriggeredRef.current = false;
      return;
    }
    onPersonClick?.(person);
  };

  useEffect(() => {
    return () => {
      if (moveListenerRef.current) {
        window.removeEventListener('mousemove', moveListenerRef.current);
      }
      if (upListenerRef.current) {
        window.removeEventListener('mouseup', upListenerRef.current);
      }
    };
  }, []);

  return (
    <div
      className="card py-4 px-4 w-full flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-2xl shadow transition-all lg:max-w-none"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      style={{
        transform: `translate3d(${swipeX}px, 0, 0)`,
        transition: swiping ? 'none' : 'transform 0.2s ease-out',
        touchAction: 'pan-y',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        boxShadow: '4px 0 0 rgba(239, 68, 68, 0.6), -4px 0 0 rgba(34, 197, 94, 0.7)'
      }}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center">
          <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
            {person.name}
          </h3>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-2 px-3 py-1 rounded-lg font-semibold text-sm ${getBalanceColor(person.balance)}`}>
          {getBalanceIcon(person.balance)}
          <span>{getBalanceText(person.balance)}</span>
        </div>
      </div>
    </div>
  );
});

PersonTile.displayName = 'PersonTile';

const DebtList = ({
  debts = [],
  onPersonClick,
  onAddDebt,
  onAddCredit
}) => {
  // Get unique persons and calculate their balances
  const allPersons = useMemo(() => {
    const personMap = new Map();

    debts.forEach(debt => {
      const personName = debt.personName;
      if (!personMap.has(personName)) {
        personMap.set(personName, {
          name: personName,
          balance: 0,
          transactions: []
        });
      }

      const person = personMap.get(personName);
      person.transactions.push(debt);

      // Calculate balance: positive for credits (user should receive), negative for debts (user should pay)
      if (debt.type === 'credit') {
        person.balance += debt.amount;
      } else {
        person.balance -= debt.amount;
      }
    });

    const personsWithLatestDate = Array.from(personMap.values()).map(person => {
      const latestTransaction = person.transactions.reduce((latest, current) => {
        const currentDateTime = new Date(current.date + 'T' + (current.time || '00:00'));
        const latestDateTime = new Date(latest.date + 'T' + (latest.time || '00:00'));
        return currentDateTime > latestDateTime ? current : latest;
      }, person.transactions[0]);

      return {
        ...person,
        latestDate: new Date(latestTransaction.date + 'T' + (latestTransaction.time || '00:00')),
        balance: normalizeBalance(person.balance)
      };
    });

    return personsWithLatestDate
      .sort((a, b) => {
        const aSettled = a.balance === 0;
        const bSettled = b.balance === 0;

        if (aSettled !== bSettled) {
          return aSettled ? 1 : -1;
        }

        return b.latestDate - a.latestDate;
      })
      .map(({ latestDate: _ignoredLatestDate, ...person }) => person);
  }, [debts]);

  const [showSettled, setShowSettled] = useState(false);

  const visiblePersons = useMemo(() => showSettled
    ? allPersons
    : allPersons.filter(p => p.balance !== 0), [showSettled, allPersons]);

  const hasSettledPersons = useMemo(() => allPersons.some(p => p.balance === 0), [allPersons]);

  const { differenceLabel, differenceBadgeClasses } = useMemo(() => {
    const netDifference = debts.reduce((total, debt) => {
      if (!debt || typeof debt !== 'object') {
        return total;
      }

      const rawAmount = typeof debt.amount === 'number' ? debt.amount : parseFloat(debt.amount);
      const amount = Number.isFinite(rawAmount) ? rawAmount : 0;
      const impact = debt.type === 'credit' ? amount : -amount;

      return total + impact;
    }, 0);

    const roundedDifference = Number.isFinite(netDifference) ? Math.round(netDifference) : 0;
    const formatter = new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    });

    const badgeClasses = roundedDifference > 0
      ? 'text-green-700 bg-green-50 border-green-200 dark:text-green-300 dark:bg-green-900/30 dark:border-green-700'
      : roundedDifference < 0
        ? 'text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-900/30 dark:border-red-700'
        : 'text-gray-700 bg-gray-50 border-gray-200 dark:text-gray-300 dark:bg-gray-900/30 dark:border-gray-700';

    return {
      differenceLabel: formatter.format(Math.abs(roundedDifference)),
      differenceBadgeClasses: badgeClasses
    };
  }, [debts]);

  if (allPersons.length === 0) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Nessun debito o credito
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Inizia ad aggiungere movimenti di debito o credito
        </p>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            onClick={() => {
              console.log('DebtList: Nuovo Debito clicked (empty state)');
              onAddDebt();
            }}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 backdrop-blur-sm text-white rounded-xl shadow-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-red-500/25 active:scale-95 font-medium"
          >
            <TrendingDown className="w-4 h-4" />
            <span>Nuovo Debito</span>
          </button>
          <button
            type="button"
            onClick={() => {
              console.log('DebtList: Nuovo Credito clicked (empty state)');
              onAddCredit();
            }}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 backdrop-blur-sm text-white rounded-xl shadow-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-green-500/25 active:scale-95 font-medium"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Nuovo Credito</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header con pulsanti - stile identico alle transazioni */}
      <div className="sticky top-20 lg:top-4 z-20 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl w-full px-6 py-3 mb-6 shadow-lg lg:max-w-none">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Debiti e Crediti</h2>
          <div className="flex items-center gap-2 text-right text-[11px] font-semibold leading-tight">
            <span className={`px-2 py-0.5 rounded-lg border shadow-sm ${differenceBadgeClasses}`}>
              {differenceLabel}
            </span>
          </div>
        </div>

        {/* Bottoni per aggiungere debiti/crediti - stile identico alle transazioni */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              console.log('DebtList: Nuovo Debito clicked');
              onAddDebt();
            }}
            className="flex-1 flex items-center justify-center gap-2 px-2 py-2 sm:px-4 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-red-500 to-red-600 backdrop-blur-sm text-white rounded-xl shadow-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-red-500/25 active:scale-95 font-medium"
          >
            <TrendingDown className="w-4 h-4" />
            <span>Nuovo Debito</span>
          </button>
          <button
            type="button"
            onClick={() => {
              console.log('DebtList: Nuovo Credito clicked');
              onAddCredit();
            }}
            className="flex-1 flex items-center justify-center gap-2 px-2 py-2 sm:px-4 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-green-500 to-green-600 backdrop-blur-sm text-white rounded-xl shadow-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-green-500/25 active:scale-95 font-medium"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Nuovo Credito</span>
          </button>
        </div>
      </div>

      {/* Lista persone */}
      <div className="space-y-3">
        {visiblePersons.length > 0 ? (
          visiblePersons.map((person) => (
            <PersonTile
              key={person.name}
              person={person}
              onPersonClick={onPersonClick}
              onAddDebt={onAddDebt}
              onAddCredit={onAddCredit}
            />
          ))
        ) : (
          !hasSettledPersons && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Nessuna persona trovata
            </div>
          )
        )}
      </div>

      {/* Bottone Mostra/Nascondi persone in pari */}
      {hasSettledPersons && (
        <div className="flex justify-center pt-2 pb-4">
          <button
            type="button"
            onClick={() => setShowSettled(prev => !prev)}
            className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all active:scale-95 transform flex items-center gap-1"
          >
            {showSettled ? 'Nascondi persone in pari' : 'Mostra persone in pari'}
            {showSettled ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      )}
    </div>
  );
};

export default DebtList; 
